/**
 * Phone number hashing utility for contact sync
 *
 * IMPORTANT: All phone numbers are normalized and hashed client-side
 * before being sent to the server. This ensures privacy.
 */

import { sha256 } from 'js-sha256';

/**
 * Normalize a phone number to E.164 format
 * Examples:
 * - "06 12 34 56 78" → "+33612345678"
 * - "0612345678" → "+33612345678"
 * - "+33 6 12 34 56 78" → "+33612345678"
 *
 * @param phone Raw phone number
 * @param defaultCountryCode Default country code (default: +33 for France)
 */
export function normalizePhoneNumber(phone: string, defaultCountryCode: string = '+33'): string {
  // Remove all non-digit characters except +
  let normalized = phone.replace(/[^\d+]/g, '');

  // Handle French numbers starting with 0
  if (normalized.startsWith('0') && !normalized.startsWith('00')) {
    normalized = defaultCountryCode + normalized.substring(1);
  }

  // Handle numbers starting with 00 (international prefix)
  if (normalized.startsWith('00')) {
    normalized = '+' + normalized.substring(2);
  }

  // Ensure it starts with +
  if (!normalized.startsWith('+')) {
    normalized = defaultCountryCode + normalized;
  }

  return normalized;
}

/**
 * Hash a normalized phone number using SHA-256
 * Uses Web Crypto API when available (HTTPS), falls back to js-sha256 (HTTP)
 *
 * @param normalizedPhone Phone number in E.164 format
 */
export async function hashPhoneNumber(normalizedPhone: string): Promise<string> {
  const input = normalizedPhone.toLowerCase();

  // Check if Web Crypto API is available (requires HTTPS)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fall through to js-sha256
    }
  }

  // Fallback for HTTP contexts (dev mode, Android WebView with live reload)
  return sha256(input);
}

/**
 * Normalize and hash a phone number
 * This is the main function to use for contact sync
 *
 * @param phone Raw phone number
 * @param defaultCountryCode Default country code
 */
export async function normalizeAndHashPhone(
  phone: string,
  defaultCountryCode: string = '+33'
): Promise<string> {
  const normalized = normalizePhoneNumber(phone, defaultCountryCode);
  return hashPhoneNumber(normalized);
}

/**
 * Validate that a phone number looks valid
 * Basic validation before processing
 */
export function isValidPhoneNumber(phone: string): boolean {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Must have at least 6 digits
  const digitsOnly = cleaned.replace(/\D/g, '');
  if (digitsOnly.length < 6) return false;

  // Should not be too long
  if (digitsOnly.length > 15) return false;

  return true;
}

export interface RawContact {
  name?: string;
  phones: string[];
}

export interface HashedContact {
  hash: string;
  name?: string;
}

/**
 * Process a list of contacts and return hashed versions
 * Filters out invalid numbers and deduplicates
 *
 * @param contacts Array of contacts with phone numbers
 * @param defaultCountryCode Default country code
 */
export async function hashContacts(
  contacts: RawContact[],
  defaultCountryCode: string = '+33'
): Promise<HashedContact[]> {
  const seenHashes = new Set<string>();
  const hashedContacts: HashedContact[] = [];

  for (const contact of contacts) {
    for (const phone of contact.phones) {
      if (!isValidPhoneNumber(phone)) continue;

      try {
        const hash = await normalizeAndHashPhone(phone, defaultCountryCode);

        // Skip duplicates
        if (seenHashes.has(hash)) continue;
        seenHashes.add(hash);

        hashedContacts.push({
          hash,
          name: contact.name,
        });
      } catch (error) {
        // Skip invalid numbers
        console.warn('Failed to hash phone number:', error);
      }
    }
  }

  return hashedContacts;
}
